"use client";

import { Calendar, User, ArrowLeft, Tag as TagIcon, Folder } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

function SinglePostConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto space-y-8">
        <div className="h-6 w-32 bg-blue-100/40 animate-pulse rounded-md" />
        <div className="h-14 w-3/4 bg-blue-100/40 animate-pulse rounded-md" />
        <div className="h-6 w-1/2 bg-blue-100/40 animate-pulse rounded-md" />
        <div className="aspect-[16/9] w-full bg-blue-100/40 animate-pulse rounded-lg" />
        <div className="space-y-3">
          <div className="h-4 w-full bg-blue-100/40 animate-pulse rounded-md" />
          <div className="h-4 w-full bg-blue-100/40 animate-pulse rounded-md" />
          <div className="h-4 w-5/6 bg-blue-100/40 animate-pulse rounded-md" />
          <div className="h-4 w-4/6 bg-blue-100/40 animate-pulse rounded-md" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto text-center py-16">
        <p className="text-slate-600 font-default">Unable to load the article at this time.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="max-w-4xl mx-auto text-center py-16">
        <p className="text-slate-600 font-default">Article not found.</p>
        <Link to="/HomePage" className="inline-block mt-6">
          <Button variant="outline" className="border-blue-600 text-blue-700 hover:bg-blue-50">
            <ArrowLeft className="size-4" />
            Back to Home
          </Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  const formattedDate = post.date
    ? new Date(post.date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  const authorAvatarUrl =
    post.author?.avatar?.["96"] ||
    post.author?.avatar?.["48"] ||
    post.author?.avatar?.["24"] ||
    "";
  const authorInitial = post.author?.name ? post.author.name.charAt(0).toUpperCase() : "D";

  return (
    <article className="max-w-4xl mx-auto">
      {}
      <div className="mb-8">
        <Link
          to="/HomePage"
          className="inline-flex items-center gap-2 text-sm text-blue-700 hover:text-blue-900 transition-colors font-default uppercase tracking-widest"
        >
          <ArrowLeft className="size-4" />
          Back to Home
        </Link>
      </div>

      {}
      {post.categories && post.categories.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-6">
          {post.categories.map((cat, i) => (
            <span
              data-index={i}
              key={cat.id}
              className="inline-flex items-center gap-1.5 text-xs font-default uppercase tracking-widest text-blue-600"
            >
              <Folder className="size-3" />
              {cat.name}
            </span>
          ))}
        </div>
      )}

      {}
      <h1
        className="font-default font-bold text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight text-slate-900 mb-6"
        dangerouslySetInnerHTML={{ __html: post.title }}
      />

      {}
      {post.excerpt && (
        <div
          className="font-default text-lg md:text-xl text-slate-700 leading-relaxed mb-10 max-w-3xl"
          dangerouslySetInnerHTML={{ __html: post.excerpt }}
        />
      )}

      {}
      <div className="flex flex-wrap items-center gap-6 pb-8 border-b border-slate-200">
        {post.author && (
          <div className="flex items-center gap-3">
            <Avatar className="size-10 rounded-lg">
              {authorAvatarUrl && (
                <AvatarImage src={authorAvatarUrl} alt={post.author.name} />
              )}
              <AvatarFallback className="rounded-lg bg-blue-600 text-white font-default font-semibold">
                {authorInitial}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col">
              <span className="text-xs font-default uppercase tracking-widest text-slate-500">
                Expert
              </span>
              <span className="text-sm font-default font-semibold text-slate-900">
                {post.author.name}
              </span>
            </div>
          </div>
        )}

        {formattedDate && (
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center size-10 rounded-lg bg-blue-50">
              <Calendar className="size-4 text-blue-600" />
            </div>
            <div className="flex flex-col">
              <span className="text-xs font-default uppercase tracking-widest text-slate-500">
                Published
              </span>
              <span className="text-sm font-default font-semibold text-slate-900">
                {formattedDate}
              </span>
            </div>
          </div>
        )}
      </div>

      {}
      {post.featuredImage && (
        <div className="my-10 overflow-hidden rounded-lg shadow-lg">
          <img
            src={post.featuredImage}
            alt={post.title}
            loading="lazy"
            decoding="async"
            className="w-full h-auto object-cover"
          />
        </div>
      )}

      {}
      {post.content && (
        <div
          className="font-default prose max-w-none text-base md:text-lg leading-relaxed text-slate-800 space-y-6 [&_h2]:font-default [&_h2]:text-3xl [&_h2]:font-bold [&_h2]:text-slate-900 [&_h2]:mt-12 [&_h2]:mb-4 [&_h3]:font-default [&_h3]:text-2xl [&_h3]:font-bold [&_h3]:text-slate-900 [&_h3]:mt-10 [&_h3]:mb-3 [&_p]:mb-6 [&_a]:text-blue-600 [&_a]:underline [&_a]:underline-offset-4 [&_a:hover]:text-blue-800 [&_strong]:text-slate-900 [&_strong]:font-bold [&_blockquote]:border-l-4 [&_blockquote]:border-blue-600 [&_blockquote]:pl-6 [&_blockquote]:italic [&_blockquote]:text-slate-600 [&_blockquote]:my-8 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:space-y-2 [&_img]:rounded-lg [&_img]:my-8"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      )}

      {}
      {post.tags && post.tags.length > 0 && (
        <div className="mt-12 pt-8 border-t border-slate-200">
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2 text-xs font-default uppercase tracking-widest text-slate-500">
              <TagIcon className="size-3.5" />
              Topics
            </span>
            {post.tags.map((tag, i) => (
              <span
                data-index={i}
                key={tag.id}
                className="px-3 py-1 text-xs font-default uppercase tracking-wider bg-slate-100 text-slate-700 border border-slate-200 hover:border-blue-600 transition-colors rounded-md"
              >
                {tag.name}
              </span>
            ))}
          </div>
        </div>
      )}

      <Separator className="my-12 bg-slate-200" />

      {}
      {post.author && (
        <Card className="bg-slate-50 border border-slate-200 shadow-md">
          <CardContent className="flex flex-col sm:flex-row items-start gap-6 p-8">
            <Avatar className="size-20 rounded-lg shrink-0">
              {authorAvatarUrl && (
                <AvatarImage src={authorAvatarUrl} alt={post.author.name} />
              )}
              <AvatarFallback className="rounded-lg bg-blue-600 text-white text-2xl font-default font-semibold">
                {authorInitial}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <span className="text-xs font-default uppercase tracking-widest text-blue-600">
                About the Expert
              </span>
              <h3 className="font-default font-bold text-2xl text-slate-900 mt-2 mb-3">
                {post.author.name}
              </h3>
              <p className="font-default text-sm text-slate-600 leading-relaxed">
                Certified environmental specialist at MYB Services with 26 years of experience in mold detection, air quality testing, and remediation protocols.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {}
      <div className="mt-16 text-center">
        <span className="text-xs font-default uppercase tracking-widest text-slate-500">
          Need Assistance?
        </span>
        <h3 className="font-default font-bold text-3xl md:text-4xl text-slate-900 mt-3 mb-6">
          Schedule Your Professional Inspection
        </h3>
        <Link to="/ContactPage">
          <Button size="lg" className="font-default uppercase tracking-widest bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow-md">
            Contact MYB Services
          </Button>
        </Link>
      </div>
    </article>
  );
}

export default function Post() {
  const currentWPQueryParams =
    typeof window !== "undefined" && (window as any).wvcClient
      ? (window as any).wvcClient.getCurrentWPQueryParams()
      : {};
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="2900"
      id="single-post-template-section"
      className="bg-white text-slate-900 py-16 md:py-24 px-4 sm:px-6 lg:px-8"
    >
      <PostsProvider wp_query={wp_query}>
        <SinglePostConsumer />
      </PostsProvider>
    </section>
  );
}